import {
  type EventSourceMessage,
  fetchEventSource,
} from '@microsoft/fetch-event-source';

import {
  type FindAllResponseProjectBlocksResponseDTO,
  type ResponseProjectBlock,
  ResponseProjectBlockModel,
} from '../../../entities/ResponseProjectBlock';
import { magellanClient } from '../../../infra/axios';
import type {
  CreateResponseProjectBlockDTO,
  CreateResponseProjectBlockThreadMessageRequestDTO,
  CreateResponseProjectBlockThreadMessageResponseDTO,
  CreateResponseProjectBlockThreadResponseDTO,
  GetResponseProjectBlockThreadByIdResponseDTO,
  UpdateResponseProjectBlockDTO,
} from './dto';

export async function getResponseProjectBlocks(projectId: number): Promise<{
  blockGenerationStatus: FindAllResponseProjectBlocksResponseDTO['blockGenerationStatus'];
  blocks: ResponseProjectBlock[];
}> {
  const response =
    await magellanClient.get<FindAllResponseProjectBlocksResponseDTO>(
      `responseProjects/${projectId}/blocks`,
    );

  return {
    blockGenerationStatus: response.data.blockGenerationStatus,
    blocks: response.data.blocks.map(
      block => new ResponseProjectBlockModel(block),
    ),
  };
}

export async function createResponseProjectBlock(
  projectId: number,
  payload: CreateResponseProjectBlockDTO,
): Promise<void> {
  await magellanClient.post<void>(
    `responseProjects/${projectId}/blocks`,
    payload,
  );
}

export async function updateResponseProjectBlock(
  projectId: number,
  blockId: number,
  payload: UpdateResponseProjectBlockDTO,
): Promise<void> {
  await magellanClient.put<void>(
    `responseProjects/${projectId}/blocks/${blockId}`,
    payload,
  );
}

export async function getResponseProjectBlockThreadById({
  blockId,
  threadId,
}: {
  blockId: number;
  threadId: number;
}): Promise<GetResponseProjectBlockThreadByIdResponseDTO> {
  const response =
    await magellanClient.get<GetResponseProjectBlockThreadByIdResponseDTO>(
      `responseProjectBlocks/${blockId}/assistant/threads/${threadId}`,
    );

  return response.data;
}

export async function createResponseProjectBlockThread({
  blockId,
}: {
  blockId: number;
}): Promise<CreateResponseProjectBlockThreadResponseDTO> {
  const response =
    await magellanClient.post<CreateResponseProjectBlockThreadResponseDTO>(
      `responseProjectBlocks/${blockId}/assistant/threads`,
    );

  return response.data;
}

export async function createResponseProjectBlockThreadMessage({
  blockId,
  threadId,
  payload,
}: {
  threadId: number;
  blockId: number;
  payload: CreateResponseProjectBlockThreadMessageRequestDTO;
}): Promise<CreateResponseProjectBlockThreadMessageResponseDTO> {
  const response =
    await magellanClient.post<CreateResponseProjectBlockThreadMessageResponseDTO>(
      `responseProjectBlocks/${blockId}/assistant/threads/${threadId}/messages`,
      payload,
    );

  return response.data;
}

export async function generateResponseProjectBlockAnswerStream({
  blockId,
  threadId,
  onmessage,
  onclose,
  onerror,
  signal,
}: {
  threadId: number;
  blockId: number;
  onmessage: (event: EventSourceMessage) => void;
  onclose: () => void;
  onerror: (error: Error) => void;
  signal: AbortSignal;
}) {
  const url =
    import.meta.env.VITE_MAGELLAN_BASE_URL +
    `/responseProjectBlocks/${blockId}/assistant/threads/${threadId}/generateAnswer`;

  return fetchEventSource(url, {
    method: 'GET',
    signal,
    onmessage,
    onclose,
    onerror,
  });
}
